import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import useTabSync from "../customHooks/useTabSync";
import { setDefaultTimezone } from "../helpers/dayjsSetup";
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import { queryClient } from '../react-query-client';
import AuthService from "../services/Auth/AuthService";
import axios from "../services/axios";
import { PUBLIC_PATHS, REFRESH_URL } from "../store/Auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const { resetGlobalFilters, setFilter } = useGlobalFilters();
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const refreshTimer = useRef(null);
    const isRefreshingRef = useRef(false);
    const applyPreferredFiltersRef = useRef(false);
    const tabSync = useTabSync({
        onLogin: () => window.location.replace("/"),
        onLogout: () => window.location.replace("/login"),
        onOrgSwitch: () => window.location.reload()
    });

    const setFilterFromAuth = (preferredWarehouseIds) => {
        setFilter("warehouseIds", preferredWarehouseIds);
    };

    const silentLogout = useCallback(() => {
        if (refreshTimer.current) {
            clearTimeout(refreshTimer.current);
        }
        resetGlobalFilters();
        queryClient.clear();
        AuthService.clearAccessToken();
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const getUserDetails = useCallback(async () => {
        try {
            const response = await AuthService.getUserDetails();
            if (response?.data) {
                const userData = response.data;
                setUser(userData);
                if (userData?.timezone) {
                    setDefaultTimezone(userData.timezone);
                }
                if (applyPreferredFiltersRef.current && userData?.preferredWarehouseIDs?.length) {
                    setFilterFromAuth(userData.preferredWarehouseIDs);
                    applyPreferredFiltersRef.current = false;
                }
                setIsAuthenticated(true);
                return response;
            }
        } catch (error) {
            console.error("Failed to get user details", error);
            silentLogout();
            return null;
        }
    }, [silentLogout]);

    const scheduleTokenRefresh = useCallback((exp) => {
        let refreshTime = exp * 1000 - Date.now() - 60000;
        if (refreshTime < 0) {
            refreshTime = 0;
        }
        if (refreshTimer.current) {
            clearTimeout(refreshTimer.current);
        }
        refreshTimer.current = setTimeout(async () => {
            if (isRefreshingRef.current) return;
            isRefreshingRef.current = true;
            try {
                const response = await axios.post(REFRESH_URL, {}, { withCredentials: true });
                const newToken = response.data?.token;
                if (!newToken) {
                    return silentLogout();
                }
                AuthService.setAccessToken(newToken);
                const decoded = AuthService.decodeJwt();
                scheduleTokenRefresh(decoded.exp);
                await getUserDetails();
            } catch {
                silentLogout();
            } finally {
                isRefreshingRef.current = false;
            }
        }, refreshTime);
    }, [getUserDetails, silentLogout]);

    const setAuthFromToken = useCallback(async (token) => {
        AuthService.setAccessToken(token);
        const decoded = AuthService.decodeJwt();
        if (!decoded?.exp) {
            silentLogout();
            return;
        }
        scheduleTokenRefresh(decoded.exp);
        return await getUserDetails();
    }, [scheduleTokenRefresh, getUserDetails, silentLogout]);

    const initializeAuth = useCallback(async () => {
        if (PUBLIC_PATHS.includes(window.location.pathname)) {
            setLoading(false);
            return;
        }
        try {
            const localToken = AuthService.getAccessToken();
            if (localToken && !AuthService.isTokenExpired()) {
                applyPreferredFiltersRef.current = false;
                await setAuthFromToken(localToken);
            } else {
                const response = await axios.post(REFRESH_URL, {}, { withCredentials: true });
                const newToken = response.data?.token;
                if (newToken) {
                    applyPreferredFiltersRef.current = false;
                    await setAuthFromToken(newToken);
                } else {
                    silentLogout();
                }
            }
        } catch {
            silentLogout();
        } finally {
            setLoading(false);
        }
    }, [setAuthFromToken, silentLogout]);

    const login = async (request) => {
        setLoading(true);
        try {
            const response = await AuthService.login(request);
            if (response?.data?.token) {
                applyPreferredFiltersRef.current = true;
                await setAuthFromToken(response.data.token);
                tabSync.broadcast("LOGIN");
            }
            return response;
        } finally {
            setLoading(false);
        }
    };

    const logout = useCallback(async () => {
        if (refreshTimer.current) {
            clearTimeout(refreshTimer.current);
        }
        try {
            await AuthService.logout();
        } finally {
            silentLogout();
            tabSync.broadcast("LOGOUT");
        }
    }, [silentLogout, tabSync]);

    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    return (
        <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);